import { useOutletContext } from "react-router-dom";
import { Carousel } from "react-responsive-carousel";
import { Download } from "lucide-react";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import React, { useState } from "react";
import QRCodeCarousel from "./QRCodeCarousel";
import BASE_URL from "./apibaseurl/ApiBaseUrl";

export default function LinkContext() {
  const { section } = useOutletContext<{ section: string }>();
  const [showQR, setShowQR] = useState(false);
  const [link, setLink] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [shortUrl, setShortUrl] = useState<string | null>(null);

  if (section === "carousel") {
    return <QRCodeCarousel />;
  }

  // URL validation helper
  const isValidHttpUrl = (input: string) => {
    try {
      const url = new URL(input);
      return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
      return false;
    }
  };

  // Normalize input by adding https:// if missing
  const normalizeUrl = (input: string) => {
    if (!input) return "";
    if (input.startsWith("http://") || input.startsWith("https://")) return input;
    return "https://" + input;
  };

  // Generate QR and short URL
  const handleGenerate = async () => {
    setError(null);
    setQrCodeUrl(null);
    setShortUrl(null);
    setShowQR(false);

    let normalizedLink = link.trim();
    normalizedLink = normalizeUrl(normalizedLink);

    if (!isValidHttpUrl(normalizedLink)) {
      setError("Please enter a valid URL starting with http:// or https://");
      return;
    }

    const formData = new FormData();
    formData.append("link", normalizedLink);

    setLoading(true);
    try {
      const response = await fetch(`${BASE_URL}/generate_qr_code`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const json = await response.json();
      console.log("API Response JSON:", json);

      const responseData = json.data || json; // handle both possible shapes

      if (responseData.qr_code_url && responseData.short_url) {
        setQrCodeUrl(responseData.qr_code_url);
        setShortUrl(responseData.short_url);
        setShowQR(true);
        setError(null);
      } else {
        setError("Invalid API response.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to generate QR code and Short URL.");
    } finally {
      setLoading(false);
    }
  };

  // Copy short URL to clipboard
  const handleCopy = (e) => {
    if (!shortUrl) return;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(shortUrl)
        .then(() => alert("Short URL copied to clipboard!"))
        .catch(() => alert("Failed to copy the URL"));
    } else {
      // Fallback for unsupported browsers
      // Use deprecated execCommand fallback
      try {
        const textArea = document.createElement("textarea");
        textArea.value = shortUrl;
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        const successful = document.execCommand('copy');
        document.body.removeChild(textArea);

        if (successful) {
          alert("Short URL copied to clipboard!");
        } else {
          alert("Failed to copy the URL");
        }
      } catch (err) {
        alert("Copy not supported in this browser");
      }
    }
  };

  // Download QR code image
  const handleDownloadQRCode = async () => {
    if (!qrCodeUrl) return;

    try {
      const response = await fetch(qrCodeUrl);
      if (!response.ok) throw new Error("Failed to fetch QR code image.");
      console.log("QR Code URL:", qrCodeUrl);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = qrCodeUrl.split("/").pop() || "qr_code.png";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading QR code:", error);
      alert("QR code download failed. Try again.");
    }
  };

  return (
    <>
      {/* Full Page Loader Overlay */}
      {loading && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          aria-label="Loading"
        >
          <div className="flex flex-col items-center space-y-3 bg-white rounded-lg p-6 shadow-lg">
            <svg
              className="animate-spin h-10 w-10 text-[#F47321]"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
            <span className="text-lg font-semibold text-[#F47321]">Loading...</span>
          </div>
        </div>
      )}

      <div className="w-full grid lg:grid-cols-2 grid-cols-1 justify-center">
        <div className="w-full px-3 sm:px-8 md:px-16 py-4 sm:py-6">
          <h3 className="helvetica-neue text-black !text-[1rem] lg:!text-3xl mb-3 mt-8 text-left md:text-left">
            Add your preferred links and get a QR code and a short URL
          </h3>

          <div className="flex flex-row md:flex-row items-stretch font-sans">
            <input
              type="text"
              placeholder="https://built-in QR Code Generators"
              className={`max-w-96 w-full rounded-none text-xs sm:text-sm bg-white px-2 text-black ${error ? "border-red-500 border-2" : "border border-gray-300"
                }`}
              value={link}
              onChange={(e) => setLink(e.target.value)}
              disabled={loading}
              autoComplete="off"
            />
            <button
              className="bg-[#F47321] hover:bg-[#F47321] border-white text-white rounded-none text-xs text-wrap lg:text-[20px] lg:py-[10px] lg:leading-6 py-1 px-2 btn-hover-effect uppercase disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={loading}
              onClick={handleGenerate}
              type="button"
            >
              <span>Generate QR Code & Short URL</span>
            </button>
          </div>

          {error && <p className="text-red-600 mt-1">{error}</p>}

          {qrCodeUrl && shortUrl && showQR && (
            <div className="flex flex-col items-center mt-4 sm:mt-6 space-y-3 sm:space-y-4">
              <img src={qrCodeUrl} alt="QR Code" className="w-24 sm:w-32 md:w-40 mb-2" />
              <div className="flex flex-row sm:flex-row items-center gap-1 sm:gap-2">
                <button
                  className="bg-[#156E77] btn-hover-green hover:bg-[#156E77] text-white uppercase flex items-center lg:gap-1 gap-2 rounded-none transition transform scale-y-125 text-xs lg:text-[18px] lg:px-4 lg:py-3 px-2 py-2"
                  onClick={handleDownloadQRCode}
                  type="button"
                >
                  <span>Download</span>
                  <span className="icon-arrow-right">
                    <Download className="w-2 h-2 lg:w-4 lg:h-4" />
                  </span>
                </button>

                <button
                  className="uppercase text-[#156E77] hover:no-underline transition transform scale-y-125 text-xs lg:text-[18px] lg:px-4 lg:py-3 px-2 py-2"
                  onClick={handleCopy}
                  type="button"
                >
                  Copy Short URL
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="w-full text-center px-8 lg:block hidden bg-white">
          <h3
            style={{ transform: "scaleY(1.2)" }}
            className="uppercase text-[#234042] text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-[60px] leading-[1.2] text-left mb-4 mt-4"
          >
            Built-in{" "}
            <span
              className="text-white font-[Bebas Neue] tracking-[2px] uppercase"
              style={{ WebkitTextStroke: "1px #F47321" }}
            >
              QR CODE
            </span>
            <br />
            Generators Simplified
          </h3>

            <img src="/images/4308850 1.svg"  className="w-full max-w-md mx-auto" />

          {/* <Carousel
            autoPlay
            infiniteLoop
            interval={2000}
            showThumbs={false}
            showArrows={false}
            showStatus={false}
            swipeable={true}
            className="w-full max-w-md mx-auto"
          >
            <div>
              <img src="/images/4308850 1.svg" alt="QR 1" />
            </div>
            <div>
              <img src="/images/4308850 1.svg" alt="QR 2" />
            </div>
            <div>
              <img src="/images/4308850 1.svg" alt="QR 3" />
            </div>
          </Carousel> */}
        </div>
      </div>
    </>
  );
}
